import React, { useState, useEffect, useRef } from 'react';
import { Edit, RotateCcw, Plus, Users, Settings, Target, FileText, Redo, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose, DrawerFooter, DrawerDescription } from './ui/drawer';
import svgPaths from '../imports/svg-xl0ue8zswv';
import { buttonHover, buttonTap, fadeInUp, staggerContainer } from '../constants/game';

// Header Components
export function StatusBar() {
  return (
    <div className="h-11 overflow-clip relative shrink-0 w-full">
      <div className="absolute h-[11.337px] right-[14.673px] top-[17.331px] w-[66.66px]">
        <div className="absolute bottom-0 left-0 right-[-0.002%] top-0">
          <svg
            className="block size-full"
            fill="none"
            preserveAspectRatio="none"
            viewBox="0 0 67 12"
          >
            <g>
              <path
                d={svgPaths.p2bcf2900}
                opacity="0.35"
                stroke="black"
              />
              <path
                d={svgPaths.p32eac000}
                fill="black"
                opacity="0.4"
              />
              <path d={svgPaths.pb5036c0} fill="black" />
              <path
                clipRule="evenodd"
                d={svgPaths.p3becb880}
                fill="black"
                fillRule="evenodd"
              />
              <path
                clipRule="evenodd"
                d={svgPaths.p3e2de00}
                fill="black"
                fillRule="evenodd"
              />
            </g>
          </svg>
        </div>
      </div>
      <div className="absolute h-[21px] left-[21px] rounded-3xl top-3 w-[54px]">
        <div className="absolute font-semibold h-5 leading-[0] left-[27px] not-italic text-black text-[15px] text-center top-px tracking-[-0.5px] translate-x-[-50%] w-[54px]">
          <p className="block leading-[20px]">9:41</p>
        </div>
      </div>
    </div>
  );
}

interface TopNavigationProps {
  teamA: string;
  teamB: string;
  gameTitle: string;
  onEditTeam: (team: "A" | "B") => void;
  onOpenSettings: () => void;
  onOpenGames: () => void;
  onOpenScorecard: () => void;
  oversPerInnings: number;
  numberOfPlayers: number;
}

export function TopNavigation({
  teamA,
  teamB,
  gameTitle,
  onEditTeam,
  onOpenSettings,
  onOpenGames,
  onOpenScorecard,
  oversPerInnings,
  numberOfPlayers,
}: TopNavigationProps) {
  return (
    <div className="h-11 relative shrink-0 w-full">
      {/* Left Arrow */}
      <div className="absolute left-4 top-0 h-full flex items-center">
        <motion.button 
          onClick={onOpenGames} 
          className="text-blue-500 hover:opacity-70 transition-opacity"
          whileHover={buttonHover}
          whileTap={buttonTap}
        >
          <ArrowLeft className="w-5 h-5" />
        </motion.button>
      </div>
      
      {/* Right side - Format + Icons */}
      <div className="absolute right-4 top-0 h-full flex items-center gap-4">
        {/* Game Format Info */}
        <motion.div 
          className="bg-blue-50 px-2 py-1 rounded-md text-right"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ 
            type: "spring",
            stiffness: 300,
            damping: 20,
            delay: 0.1
          }}
          whileHover={{ scale: 1.05 }}
        >
          <p className="text-[10px] text-blue-700 font-medium leading-[12px]">
            {oversPerInnings} overs
          </p>
          <p className="text-[10px] text-blue-700 font-medium leading-[12px]">
            {numberOfPlayers} players
          </p>
        </motion.div>

        {/* Action Icons */}
        <div className="flex gap-3">
          <motion.button
            onClick={onOpenScorecard}
            className="text-blue-500 text-[17px] hover:opacity-70 transition-opacity"
            title="View Scorecard"
            whileHover={buttonHover}
            whileTap={buttonTap}
          >
            <FileText className="w-4 h-4" />
          </motion.button>
          <motion.button
            onClick={onOpenSettings}
            className="text-blue-500 text-[17px] hover:opacity-70 transition-opacity"
            whileHover={buttonHover}
            whileTap={buttonTap}
          >
            <Settings className="w-4 h-4" />
          </motion.button>
        </div>
      </div>
      
      {/* Center content - Game title and teams */}
      <div className="absolute left-[60px] right-[120px] top-0 h-full flex flex-col justify-center">
        <div className="flex flex-col">
          <p className="text-[17px] font-semibold tracking-[-0.408px] leading-[22px] text-left whitespace-nowrap overflow-hidden text-ellipsis">
            {gameTitle}
          </p>
          <div className="flex items-center gap-1 text-[11px] font-medium tracking-[0.06px] leading-[13px]">
            <motion.button
              onClick={() => onEditTeam("A")}
              className="hover:text-blue-500 transition-colors underline-offset-2 hover:underline"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {teamA}
            </motion.button>
            <span>v</span>
            <motion.button
              onClick={() => onEditTeam("B")}
              className="hover:text-blue-500 transition-colors underline-offset-2 hover:underline"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {teamB}
            </motion.button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface GameFormatDisplayProps {
  oversPerInnings: number;
  numberOfPlayers: number;
}

export function GameFormatDisplay({ oversPerInnings, numberOfPlayers }: GameFormatDisplayProps) {
  return (
    <motion.div 
      className="bg-blue-50 px-3 py-2 rounded-md"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ 
        type: "spring",
        stiffness: 300,
        damping: 20,
        delay: 0.1
      }}
      whileHover={{ scale: 1.05 }}
    >
      <p className="text-xs text-blue-700 font-medium">
        {oversPerInnings} overs • {numberOfPlayers} players
      </p>
    </motion.div>
  );
}

interface ChaseTargetCardProps {
  targetRuns: number;
  ballsRemaining: number;
  runRate: string;
  teamName: string;
}

export function ChaseTargetCard({ targetRuns, ballsRemaining, runRate, teamName }: ChaseTargetCardProps) {
  const oversRemaining = Math.floor(ballsRemaining / 6);
  const ballsInOver = ballsRemaining % 6;
  const oversDisplay = ballsInOver > 0 ? `${oversRemaining}.${ballsInOver}` : `${oversRemaining}`;

  return (
    <motion.div 
      className="bg-orange-50 border border-orange-200 rounded-lg p-3 w-full"
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ 
        type: "spring",
        stiffness: 300,
        damping: 25
      }}
      whileHover={{ scale: 1.02 }}
    >
      <div className="flex items-center gap-2 mb-2">
        <motion.div
          initial={{ rotate: -180, scale: 0 }}
          animate={{ rotate: 0, scale: 1 }}
          transition={{ delay: 0.2, type: "spring" }}
        >
          <Target className="w-4 h-4 text-orange-600" />
        </motion.div>
        <span className="text-sm font-medium text-orange-800">Chase Target</span>
      </div>
      <p className="text-sm text-orange-700">
        <strong>{teamName}</strong> need <strong>{targetRuns} runs</strong> to win off <strong>{oversDisplay} overs</strong>
      </p>
      <p className="text-xs text-orange-600 mt-1">
        Required run rate: {runRate}
      </p>
    </motion.div>
  );
}

interface LastManStandsCardProps {
  batterName: string;
  teamName: string;
}

export function LastManStandsCard({ batterName, teamName }: LastManStandsCardProps) {
  return (
    <motion.div 
      className="bg-purple-50 border border-purple-200 rounded-lg p-3 w-full"
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ 
        type: "spring",
        stiffness: 300,
        damping: 25
      }}
      whileHover={{ scale: 1.02 }}
    >
      <div className="flex items-center gap-2 mb-2">
        <motion.div
          initial={{ rotate: -180, scale: 0 }}
          animate={{ rotate: 0, scale: 1 }}
          transition={{ delay: 0.2, type: "spring" }}
        >
          <Users className="w-4 h-4 text-purple-600" />
        </motion.div>
        <span className="text-sm font-medium text-purple-800">Last Man Stands</span>
      </div>
      <p className="text-sm text-purple-700">
        <strong>{batterName}</strong> is batting alone for <strong>{teamName}</strong>
      </p>
      <p className="text-xs text-purple-600 mt-1">
        Game continues until the batter is out, retires, or innings ends
      </p>
    </motion.div>
  );
}

interface EditTeamDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  teamLetter: string;
  currentName: string;
  onSave: (name: string) => void;
}

export function EditTeamDrawer({ isOpen, onClose, teamLetter, currentName, onSave }: EditTeamDrawerProps) {
  const [name, setName] = useState(currentName);

  const handleSave = () => {
    if (name.trim()) {
      onSave(name.trim());
      onClose();
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent className="max-h-[85vh] flex flex-col">
        <div className="mx-auto w-full max-w-sm flex flex-col min-h-0">
          <DrawerHeader className="flex-shrink-0">
            <DrawerTitle>
              Edit Team {teamLetter} Name
            </DrawerTitle>
            <DrawerDescription>
              Enter a new name for Team {teamLetter}.
            </DrawerDescription>
          </DrawerHeader>
          
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="teamName">Team Name</Label>
                <Input
                  id="teamName"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter team name"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSave();
                    }
                  }}
                  className="text-base"
                />
              </div>
              {/* Bottom padding for safe scrolling */}
              <div className="h-4"></div>
            </div>
          </div>
          
          <DrawerFooter className="flex-shrink-0">
            <Button
              onClick={handleSave}
              disabled={!name.trim()}
            >
              Save Changes
            </Button>
            <DrawerClose asChild>
              <Button variant="outline">Cancel</Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

interface EditPlayerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  playerType: string;
  currentName: string;
  onSave: (name: string) => void;
  teamPlayers?: string[];
  onAddNewPlayer?: (name: string) => void;
}

export function EditPlayerDrawer({
  isOpen,
  onClose,
  playerType,
  currentName,
  onSave,
  teamPlayers = [],
  onAddNewPlayer,
}: EditPlayerDrawerProps) {
  const [name, setName] = useState(currentName);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newPlayerName, setNewPlayerName] = useState("");

  const handleSave = () => {
    if (name.trim()) {
      onSave(name.trim());
      onClose();
    }
  };

  const handleAddNewPlayer = () => {
    if (newPlayerName.trim() && onAddNewPlayer) {
      onAddNewPlayer(newPlayerName.trim());
      setName(newPlayerName.trim());
      setNewPlayerName("");
      setIsAddingNew(false);
    }
  };

  const getTitle = () => {
    switch (playerType) {
      case "batter1":
        return "Edit Batter 1";
      case "batter2":
        return "Edit Batter 2";
      case "bowler":
        return "Edit Bowler";
      default:
        return "Edit Player";
    }
  };

  const showPlayerSelection = teamPlayers.length > 0;

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent className="max-h-[85vh] flex flex-col">
        <div className="mx-auto w-full max-w-sm flex flex-col min-h-0">
          <DrawerHeader className="flex-shrink-0">
            <DrawerTitle>{getTitle()}</DrawerTitle>
            <DrawerDescription>
              {isAddingNew 
                ? "Add a new player to the team roster."
                : showPlayerSelection 
                  ? "Select a player from the team roster or add a new one."
                  : "Enter the player's name."
              }
            </DrawerDescription>
          </DrawerHeader>
          
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            <div className="flex flex-col gap-4">
              
              {showPlayerSelection && !isAddingNew && (
                <div className="flex flex-col gap-2">
                  <Label>Select from team roster</Label>
                  <Select value={name} onValueChange={setName}>
                    <SelectTrigger className="text-base">
                      <SelectValue placeholder="Choose a player" />
                    </SelectTrigger>
                    <SelectContent>
                      {teamPlayers.map((player, index) => (
                        <SelectItem key={index} value={player}>
                          {player}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    onClick={() => setIsAddingNew(true)}
                    className="mt-2"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add New Player
                  </Button>
                </div>
              )}

              {(!showPlayerSelection || isAddingNew) && (
                <div className="flex flex-col gap-2">
                  <Label htmlFor="playerName">
                    {isAddingNew
                      ? "New Player Name"
                      : "Player Name"}
                  </Label>
                  <Input
                    id="playerName"
                    value={isAddingNew ? newPlayerName : name}
                    onChange={(e) =>
                      isAddingNew
                        ? setNewPlayerName(e.target.value)
                        : setName(e.target.value)
                    }
                    placeholder="Enter player name"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        isAddingNew
                          ? handleAddNewPlayer()
                          : handleSave();
                      }
                    }}
                    className="text-base"
                  />
                  {isAddingNew && (
                    <Button
                      variant="outline"
                      onClick={() => setIsAddingNew(false)}
                      className="mt-2"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              )}

              {/* Bottom padding for safe scrolling */}
              <div className="h-4"></div>
            </div>
          </div>
          
          <DrawerFooter className="flex-shrink-0">
            {isAddingNew ? (
              <Button
                onClick={handleAddNewPlayer}
                disabled={!newPlayerName.trim()}
              >
                Add to Team
              </Button>
            ) : (
              <Button
                onClick={handleSave}
                disabled={!name.trim()}
              >
                Save Changes
              </Button>
            )}
            <DrawerClose asChild>
              <Button variant="outline">Cancel</Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

interface ScoringButtonProps {
  children: React.ReactNode;
  color?:
    | "gray"
    | "green"
    | "purple"
    | "red"
    | "blue"
    | "light";
  onClick: () => void;
  className?: string;
  disabled?: boolean;
}

export function ScoringButton({
  children,
  color = "gray",
  onClick,
  className = "",
  disabled = false,
}: ScoringButtonProps) {
  const colorClasses = {
    gray: "bg-[#8e8e93]",
    green: "bg-[#35a658]",
    purple: "bg-[#cf66e7]",
    red: "bg-[#ff556c]",
    blue: "bg-[#007aff]",
    light: "bg-[rgba(120,120,128,0.2)] text-black",
  };

  const textColor =
    color === "light" ? "text-black" : "text-white";

  return (
    <motion.button
      onClick={onClick}
      disabled={disabled}
      className={`${colorClasses[color]} ${textColor} basis-0 grow min-h-[60px] relative rounded-lg shrink-0 w-full ${className} transition-all ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      whileHover={disabled ? {} : { scale: 1.02, y: -2 }}
      whileTap={disabled ? {} : { scale: 0.95, y: 0 }}
      transition={{ 
        type: "spring", 
        stiffness: 400, 
        damping: 17 
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        boxShadow: disabled ? 'none' : '0 4px 8px rgba(0, 0, 0, 0.1)',
      }}
    >
      <div className="flex flex-col items-center justify-center relative size-full p-2">
        <div className="font-semibold leading-[0] not-italic relative shrink-0 text-center">
          {children}
        </div>
      </div>
    </motion.button>
  );
}

interface ScoringKeypadProps {
  onScore: (action: string, runs?: number) => void;
  canUndo: boolean;
  canRedo: boolean;
  gameComplete: boolean;
}

export function ScoringKeypad({
  onScore,
  canUndo,
  canRedo,
  gameComplete,
}: ScoringKeypadProps) {
  return (
    <motion.div 
      className="w-full mt-4"
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
    >
      <div className="flex flex-col items-center relative w-full">
        <div className="box-border content-stretch flex flex-col gap-4 items-center justify-start px-4 py-3 relative w-full">
          <div className="box-border content-stretch flex flex-row gap-1.5 items-start justify-center p-0 relative w-full">
            {/* Left column */}
            <motion.div 
              className="basis-0 box-border content-stretch flex flex-col gap-1.5 grow items-center justify-start min-h-px min-w-px p-0 relative shrink-0"
              variants={fadeInUp}
            >
              <ScoringButton
                color="gray"
                onClick={() => onScore("no_runs", 0)}
                disabled={gameComplete}
              >
                <span className="text-[18px] sm:text-[20px]">No runs</span>
              </ScoringButton>
              <ScoringButton
                color="gray"
                onClick={() => onScore("runs", 2)}
                disabled={gameComplete}
              >
                <span className="text-[28px] sm:text-[32px]">2</span>
              </ScoringButton>
              <ScoringButton
                color="green"
                onClick={() => onScore("runs", 4)}
                disabled={gameComplete}
              >
                <span className="text-[28px] sm:text-[32px]">4</span>
              </ScoringButton>
              <div className="flex flex-row gap-1.5 w-full">
                <ScoringButton
                  color={canRedo ? "green" : "blue"}
                  onClick={() => onScore(canRedo ? "redo" : "undo")}
                  className={`flex-1 ${!canUndo && !canRedo ? 'opacity-50' : ''}`}
                  disabled={!canUndo && !canRedo}
                >
                  {canRedo ? (
                    <Redo className="w-4 h-4 sm:w-5 sm:h-5" />
                  ) : (
                    <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5" />
                  )}
                </ScoringButton>
                <ScoringButton
                  color="blue"
                  onClick={() => onScore("add")}
                  className="flex-1"
                  disabled={gameComplete}
                >
                  <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                </ScoringButton>
              </div>
            </motion.div>

            {/* Middle column */}
            <motion.div 
              className="basis-0 box-border content-stretch flex flex-col gap-1.5 grow items-center justify-start min-h-px min-w-px p-0 relative shrink-0"
              variants={fadeInUp}
            >
              <ScoringButton
                color="gray"
                onClick={() => onScore("runs", 1)}
                disabled={gameComplete}
              >
                <span className="text-[28px] sm:text-[32px]">1</span>
              </ScoringButton>
              <ScoringButton
                color="gray"
                onClick={() => onScore("runs", 3)}
                disabled={gameComplete}
              >
                <span className="text-[28px] sm:text-[32px]">3</span>
              </ScoringButton>
              <ScoringButton
                color="purple"
                onClick={() => onScore("runs", 6)}
                disabled={gameComplete}
              >
                <span className="text-[28px] sm:text-[32px]">6</span>
              </ScoringButton>
              <ScoringButton
                color="red"
                onClick={() => onScore("wicket")}
                disabled={gameComplete}
              >
                <span className="text-[16px] sm:text-[20px]">Wicket</span>
              </ScoringButton>
            </motion.div>

            {/* Right column */}
            <motion.div 
              className="basis-0 box-border content-stretch flex flex-col gap-1.5 grow items-center justify-start min-h-px min-w-px p-0 relative shrink-0"
              variants={fadeInUp}
            >
              <ScoringButton
                color="light"
                onClick={() => onScore("leg_bye")}
                disabled={gameComplete}
              >
                <span className="text-[14px] sm:text-[18px]">Leg bye</span>
              </ScoringButton>
              <ScoringButton
                color="light"
                onClick={() => onScore("no_ball")}
                disabled={gameComplete}
              >
                <span className="text-[14px] sm:text-[18px]">No ball</span>
              </ScoringButton>
              <ScoringButton
                color="light"
                onClick={() => onScore("bye")}
                disabled={gameComplete}
              >
                <span className="text-[14px] sm:text-[18px]">Bye</span>
              </ScoringButton>
              <ScoringButton
                color="light"
                onClick={() => onScore("wide")}
                disabled={gameComplete}
              >
                <span className="text-[14px] sm:text-[18px]">Wide</span>
              </ScoringButton>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}